import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, OutlinedTextFieldProps } from '@mui/material';
import amplitudeLog from '@client/amplitude';
import { AMP_PROPOSAL_SELECT } from '@shared/constants';
import type { IProposal, ProposalId } from '@shared/models/proposals.model';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getCo2eChartDataFromResults } from '@ui/BarChart/helpers/bar-chart.helpers';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import InlineTextField from '@ui/InlineTextField';
import { ProposalKebabMenu } from '@features/projects/menus/ProposalKebabMenu';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import {
  useIsMenuOpen,
  useTriggerContextMenu,
} from '@client/hooks/use-menu.hooks';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { useResolvedProjectTarget } from '@features/scope/scope-targets.hooks';
import { useSetSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useRequestProposalActions } from '../hooks/use-proposal-actions-request.hooks';
import {
  elementListUrlPageFromPage,
  usePageFromUrl,
  useProjectNavigation,
} from '@client/hooks/use-router.hooks';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import {
  useSelectedElementId,
  useSelectedVersion,
} from '@client/store/ui/ui.hook';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useResultsPerGFA } from '@features/results/use-results.hooks';
import { useRenameProposal } from '@features/proposals/hooks/use-proposals.hooks';
import { useHiddenProposal } from '@features/proposals/use-proposals.hooks';
import type { Results } from '@shared/models/unit.model';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import { OverviewBarShell } from '@features/results/OverviewBarShell';
import {
  applyElementSelectionForProposalNavigation,
  hasElementSelection,
  resolveElementSelectionForProposalNavigation,
} from '../helpers/proposal-selection.helpers';
import { hasVisibleProposalTarget } from '../proposals-overview-bar-layout.helpers';

type ProposalBarProps = {
  proposal: IProposal;
  /** Immediate overview selection; URL-backed content may still be transitioning. */
  selectedProposalId?: ProposalId;
  /** Marks the clicked bar before the heavier URL/content update starts. */
  onSelectProposal: (proposalId: ProposalId) => void;
  /** Coordinated across the overview so clouds are either shown on every bar or none. */
  showCo2LabelIcon: boolean;
  /** Resolved bar fill from `getProposalSwatchColor` (palette index; baseline is separate UI). */
  barSwatchColor: string;
  /** Total results for this proposal (version-level entry from resultsRecord) */
  results: Results;
  barWidth: number;
  maxBarHeight: number;
  co2ePerGfaMax: number;
  costPerGfaMax: number;
  baselineCo2ePerGFA: number;
  baselineCostBarHeight: number;
  /** When true, opens the inline name editor on mount (for newly created proposals). */
  autoFocus?: boolean;
  tooltipPlacement?: 'left' | 'right';
  /** Called when the bar is already selected and clicked — clears group selection in the element list. */
  onDeselectGroup?: () => void;
};

/**
 * Vertical bar for a single {@link IProposal}.
 * Mirrors the v1 {@link ProposalBar} layout (via {@link OverviewBarShell})
 * but reads from the data model.
 *
 * Includes inline rename, hover state, kebab + context menu (Duplicate / Rename / Delete),
 * `autoFocus` for newly-created proposals, readonly handling, hidden-state fade
 * and Amplitude tracking — matching v1 feature parity.
 */
const ProposalBar: React.FC<ProposalBarProps> = ({
  proposal,
  selectedProposalId,
  onSelectProposal,
  showCo2LabelIcon,
  barSwatchColor,
  results,
  barWidth,
  maxBarHeight,
  co2ePerGfaMax,
  costPerGfaMax,
  baselineCo2ePerGFA,
  baselineCostBarHeight,
  autoFocus,
  tooltipPlacement = 'right',
  onDeselectGroup,
}) => {
  const { id } = proposal;
  const target = useResolvedProjectTarget();

  const readonly = useIsReadonly();
  const setSelectedProposalId = useSetSelectedProposalId();
  const requestProposalActions = useRequestProposalActions();
  const navigateTo = useProjectNavigation();
  const pageFromUrl = usePageFromUrl();
  const version = useSelectedVersion();
  const selectedElementId = useSelectedElementId();
  const displayCurrency = useDisplayCurrency();
  const renameProposal = useRenameProposal();

  const hidden = useHiddenProposal(id);
  const isKebabMenuOpen = useIsMenuOpen(id);
  const triggerContextMenu = useTriggerContextMenu(id);

  const isSelected = selectedProposalId === proposal.id;
  // uses URL-based selection — treat the URL-selected proposal as "active"
  // so the name area renders the tab-style background like v1.
  const active = isSelected;
  const color = !hidden ? barSwatchColor : 'rgba(0,0,0,0.08)';

  const resultsPerGFA = useResultsPerGFA(results);
  const co2ePerGFA = getConversionFactorValue(resultsPerGFA, 'co2e');
  const costPerGFA = getConversionFactorValue(resultsPerGFA, 'sek');
  const diffValue = hasVisibleProposalTarget(target)
    ? co2ePerGFA - target
    : undefined;

  const co2eChartData = useMemo(
    () => getCo2eChartDataFromResults(resultsPerGFA),
    [resultsPerGFA],
  );

  const costBarHeight = useMemo(
    () =>
      displayCurrency.isEnabled && costPerGfaMax > 0
        ? (costPerGFA / costPerGfaMax) * maxBarHeight
        : 0,
    [displayCurrency.isEnabled, costPerGFA, costPerGfaMax, maxBarHeight],
  );

  const costLabel = useMemo(
    () =>
      displayCurrency.isEnabled && costPerGFA > 0
        ? getDisplayValueData(
            { value: costPerGFA, unit: 'sek' },
            { outputUnit: displayCurrency.current, notation: 'compact' },
          ).value
        : undefined,
    [displayCurrency, costPerGFA],
  );

  const [hover, startHover, endHover] = useBooleanState();
  const [isEditingName, startEditingName, stopEditingName] =
    useBooleanState(false);
  const [kebabMenuAnchor, setKebabMenuAnchor] = useState<Element>();

  const handleClick = useMouseEventCallback(
    () => {
      // Shared / view-only: switch viewed proposal; edits stay behind readonly checks below.
      if (isSelected) {
        onDeselectGroup?.();
        // Second tap on the already-active proposal keeps proposal selected but clears element selection so the side panel shows proposal actions (DEV-3201).
        if (hasElementSelection(selectedElementId)) {
          void navigateTo({
            page: elementListUrlPageFromPage(pageFromUrl),
            elementId: URL_CATEGORY_NONE,
          });
        } else {
          // Nothing to clear, so no selection change tells the panel to switch.
          requestProposalActions();
        }
        return;
      }
      // Paint selection inside Proposal Overview first. The URL change also
      // re-renders the much heavier element list, so keep that work in a
      // transition instead of making the click feedback wait for it.
      onSelectProposal(proposal.id);
      startTransition(() => {
        setSelectedProposalId(proposal.id);
        applyElementSelectionForProposalNavigation(
          resolveElementSelectionForProposalNavigation(
            version,
            proposal.id,
            selectedElementId,
          ),
          navigateTo,
          elementListUrlPageFromPage(pageFromUrl),
        );
      });
      amplitudeLog(AMP_PROPOSAL_SELECT, { ProposalId: proposal.id });
    },
    { ignoreInputEvents: true },
  );

  const nameInputProps: Partial<OutlinedTextFieldProps> = useMemo(
    () => ({
      autoFocus,
      onFocus: (e) => {
        e.currentTarget.select();
      },
      InputProps: {
        inputProps: {
          // The bar has no visible label, so the name is the input's only name.
          // data-proposal-id + aria-current mark which overlay the URL applied.
          'aria-label': proposal.name,
          'data-proposal-id': proposal.id,
          ...(isSelected ? { 'aria-current': true as const } : {}),
          style: { textAlign: 'center' as const, fontSize: '13px' },
        },
      },
      sx: {
        '& .MuiOutlinedInput-root': { height: 46 },
        ...(!active &&
          hover && {
            '& .MuiInputBase-input': {
              color: 'rgba(0,0,0,0.8)',
              WebkitTextFillColor: 'rgba(0,0,0,0.8)',
            },
          }),
      },
    }),
    [autoFocus, active, hover, isSelected, proposal.id, proposal.name],
  );

  const saveName = useCallback(
    async (name: string) => {
      stopEditingName();
      try {
        await renameProposal(proposal.id, name);
      } catch {
        // Reopen the editor if the save round-trip failed (mirrors v1).
        setTimeout(() => startEditingName(), 250);
      }
    },
    [proposal.id, startEditingName, stopEditingName, renameProposal],
  );

  const closeKebabMenu = useCallback(() => {
    endHover();
    setKebabMenuAnchor(undefined);
  }, [endHover]);

  const editName = useCallback(() => {
    startEditingName();
    closeKebabMenu();
  }, [closeKebabMenu, startEditingName]);

  useEffect(() => {
    autoFocus && startEditingName();
  }, [autoFocus, startEditingName]);

  const handleNameDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (readonly) return;
      startEditingName();
    },
    [readonly, startEditingName],
  );

  // Build the name-area children once; passed as ReactNode `name` to the shell.
  const nameContent = (
    <Box
      onDoubleClick={handleNameDoubleClick}
      sx={{
        width: '100%',
      }}
    >
      <InlineTextField
        value={proposal.name}
        editing={isEditingName}
        readonly={!isEditingName}
        inactive={!active && !isEditingName}
        bold
        onSave={saveName}
        onCancel={stopEditingName}
        textFieldProps={nameInputProps}
      />
      {!readonly && (hover || kebabMenuAnchor || isKebabMenuOpen) && (
        <Box
          sx={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <ProposalKebabMenu
            proposal={proposal}
            anchor={kebabMenuAnchor}
            onOpen={setKebabMenuAnchor}
            onClose={closeKebabMenu}
            onEdit={editName}
            buttonProps={{ verticalIcon: true }}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <OverviewBarShell
      barWidth={barWidth}
      maxBarHeight={maxBarHeight}
      color={color}
      co2eChartData={co2eChartData}
      co2ePerGFA={co2ePerGFA}
      co2ePerGfaMax={co2ePerGfaMax}
      backdropCo2ePerGFA={baselineCo2ePerGFA}
      backdropColor={BASELINE_COLOR}
      backdropCostBarHeight={baselineCostBarHeight}
      costBarHeight={costBarHeight}
      costLabel={costLabel}
      showInnerBorders
      outlineOnly={proposal.actionIds.length === 0}
      showCo2LabelIcon={showCo2LabelIcon}
      tooltipPlacement={tooltipPlacement}
      diffValue={diffValue}
      active={active}
      isSelected={isSelected}
      dimmed={selectedProposalId === undefined}
      onClick={handleClick}
      onContextMenu={triggerContextMenu}
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      cursor="pointer"
      name={nameContent}
      aria-label={proposal.name}
    />
  );
};

export default ProposalBar;
